import React, { useState, useEffect, useCallback } from 'react';
import { fromIso } from '../../utils/businessDate';
import { getTenantToday, getTodayDate } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Plus,
  Eye,
  BarChart3,
  PieChart,
  Wallet,
  Target,
  Banknote,
  FileText,
  Send,
  Check,
  X,
  Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

// ============== Types ==============
interface Commitment {
  id: number;
  fundId: number;
  fundName: string;
  fundType: 'PRIVATE_EQUITY' | 'VENTURE_CAPITAL' | 'REAL_ESTATE' | 'HEDGE_FUND' | 'INFRASTRUCTURE' | 'PRIVATE_CREDIT';
  portfolioId: number;
  portfolioName: string;
  commitmentAmount: number;
  calledAmount: number;
  remainingCommitment: number;
  distributedAmount: number;
  currentNAV: number;
  status: 'ACTIVE' | 'FULLY_CALLED' | 'CLOSED';
  vintage: number;
  commitmentDate: string;
}

interface CapitalCall {
  id: number;
  commitmentId: number;
  fundName: string;
  portfolioName: string;
  callAmount: number;
  callDate: string;
  dueDate: string;
  status: 'PENDING' | 'NOTICE_RECEIVED' | 'REVIEWED' | 'APPROVED' | 'FUNDING_INITIATED' | 'FUNDED' | 'RECONCILED';
  callNumber: number;
  purpose?: string;
}

interface Distribution {
  id: number;
  commitmentId?: number;
  fundName?: string;
  portfolioName?: string;
  // Backend uses grossDistribution, frontend mock uses totalAmount
  totalAmount?: number;
  grossDistribution?: number;
  netDistribution?: number;
  distributionDate: string;
  status: 'PENDING' | 'PENDING_REVIEW' | 'NOTICE_RECEIVED' | 'REVIEWED' | 'APPROVED' | 'RECEIVED' | 'RECONCILED';
  returnOfCapital?: number;
  capitalGains?: number;
  // Backend uses qualifiedDividends + unqualifiedDividends, mock uses dividends
  dividends?: number;
  qualifiedDividends?: number;
  unqualifiedDividends?: number;
  // Backend uses interestIncome, mock uses interest
  interest?: number;
  interestIncome?: number;
}

interface CommitmentWorkspace {
  commitmentId: number;
  fundId?: number;
  fundName?: string;
  fundType?: string;
  portfolioId: number;
  portfolioName?: string;
  portfolioCode?: string;
  commitmentAmount: number;
  calledAmount: number;
  unfundedCommitment: number;
  distributedAmount: number;
  recallableAmount: number;
  status: string;
  capitalCalls: Array<{
    callId: number;
    callNumber?: number;
    callAmount: number;
    dueDate?: string;
    status: string;
    paymentReference?: string;
  }>;
  distributions: Array<{
    distributionId: number;
    distributionNumber?: number;
    grossDistribution: number;
    distributionDate?: string;
    status: string;
    returnOfCapital?: number;
    capitalGains?: number;
  }>;
  recentEvents: Array<{
    eventId: number;
    eventType: string;
    status: string;
    eventDate?: string;
    eventAmount?: number;
    message?: string;
  }>;
}

// ============== Styled Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 24px;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const MetricCard = styled(GlassCard)`
  padding: 20px;
`;

// ============== Helper Functions ==============
const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${(value * 100).toFixed(1)}%`;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getFundTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    'PRIVATE_EQUITY': 'text-violet-400 bg-violet-500/20',
    'VENTURE_CAPITAL': 'text-emerald-400 bg-emerald-500/20',
    'REAL_ESTATE': 'text-amber-400 bg-amber-500/20',
    'HEDGE_FUND': 'text-blue-400 bg-blue-500/20',
    'INFRASTRUCTURE': 'text-slate-400 bg-slate-500/20',
    'PRIVATE_CREDIT': 'text-rose-400 bg-rose-500/20'
  };
  return colors[type] || 'text-slate-400 bg-slate-500/20';
};

const getCallStatusBadge = (status: string) => {
  const configs: Record<string, { bg: string; text: string; icon: React.ReactNode; label?: string }> = {
    'PENDING': { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: <Clock className="w-3 h-3" />, label: 'Pending' },
    'NOTICE_RECEIVED': { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: <Clock className="w-3 h-3" />, label: 'Notice Received' },
    'REVIEWED': { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: <Eye className="w-3 h-3" />, label: 'Reviewed' },
    'APPROVED': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <CheckCircle className="w-3 h-3" />, label: 'Approved' },
    'FUNDING_INITIATED': { bg: 'bg-indigo-500/20', text: 'text-indigo-400', icon: <Send className="w-3 h-3" />, label: 'Wire Sent' },
    'FUNDED': { bg: 'bg-violet-500/20', text: 'text-violet-400', icon: <Check className="w-3 h-3" />, label: 'Funded' },
    'RECONCILED': { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: <CheckCircle className="w-3 h-3" />, label: 'Reconciled' },
    'RECEIVED': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <Check className="w-3 h-3" />, label: 'Received' }
  };
  const config = configs[status] || configs['PENDING'];
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium", config.bg, config.text)}>
      {config.icon}
      {config.label || status.replace(/_/g, ' ')}
    </span>
  );
};

// New-commitment creation happens on the governed PE ticket
// (/trade-entry/private-equity) — this ops screen only queues and processes.

// ============== Props ==============
interface AlternativeAssetsScreenProps {
  onNavigateToDocumentInbox?: () => void;
}

// ============== Main Component ==============
const AlternativeAssetsScreen: React.FC<AlternativeAssetsScreenProps> = ({ onNavigateToDocumentInbox }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'commitments' | 'calls' | 'distributions'>('overview');
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [capitalCalls, setCapitalCalls] = useState<CapitalCall[]>([]);
  const [distributions, setDistributions] = useState<Distribution[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCommitment, setSelectedCommitment] = useState<Commitment | null>(null);
  const [expandedCommitments, setExpandedCommitments] = useState<Set<number>>(new Set());
  const [commitmentWorkspace, setCommitmentWorkspace] = useState<CommitmentWorkspace | null>(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // AI Review Panel
  const [selectedCall, setSelectedCall] = useState<CapitalCall | null>(null);
  const [selectedDistribution, setSelectedDistribution] = useState<Distribution | null>(null);

  // Aggregate metrics
  const totalCommitment = commitments.reduce((sum, c) => sum + c.commitmentAmount, 0);
  const totalCalled = commitments.reduce((sum, c) => sum + c.calledAmount, 0);
  const totalDistributed = commitments.reduce((sum, c) => sum + c.distributedAmount, 0);
  const totalNAV = commitments.reduce((sum, c) => sum + c.currentNAV, 0);
  const unfundedCommitment = totalCommitment - totalCalled;
  const pendingCalls = capitalCalls.filter(c => ['PENDING', 'NOTICE_RECEIVED', 'REVIEWED', 'APPROVED'].includes(c.status));
  const overdueCalls = capitalCalls.filter(c => new Date(c.dueDate) < fromIso(getTenantToday()) && !['FUNDED', 'RECONCILED'].includes(c.status));

  // Fetch data - each API call handles its own errors independently
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    // Fetch commitments across all portfolios
    try {
      const portfoliosRes = await axios.get('/api/dimensions/portfolios?size=100');
      const portfolios = portfoliosRes.data?.content || portfoliosRes.data || [];
      const allCommitments: Commitment[] = [];
      for (const p of portfolios) {
        try {
          const res = await axios.get(`/api/alternatives/commitments/portfolio/${p.id}`);
          const commits = res.data || [];
          for (const c of commits) {
            if (!allCommitments.find(existing => existing.id === c.id)) {
              allCommitments.push({ ...c, portfolioName: p.portfolioName || `Portfolio ${p.id}` });
            }
          }
        } catch { /* portfolio may have no commitments */ }
      }
      setCommitments(allCommitments);
    } catch (error) {
      console.error('Failed to fetch commitments:', error);
      setCommitments([]);
      setLoadError('Unable to load commitments from OMS APIs.');
    }

    // Fetch capital calls
    try {
      const callsRes = await axios.get('/api/alternatives/capital-calls?size=50');
      setCapitalCalls(callsRes.data.content || callsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch capital calls:', error);
      setCapitalCalls([]);
      setLoadError((prev) => prev || 'Unable to load capital calls from OMS APIs.');
    }

    // Fetch distributions
    try {
      const distributionsRes = await axios.get('/api/alternatives/distributions?size=50');
      console.log('Distributions API response:', distributionsRes.data);
      const distData = distributionsRes.data.content || distributionsRes.data || [];
      console.log('Distributions data to set:', distData.length, 'items');
      // Always set API data (even if empty) - don't fall back to mock for empty results
      setDistributions(distData);
    } catch (error: any) {
      console.error('Failed to fetch distributions:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Token present:', !!localStorage.getItem('token'));
      setDistributions([]);
      setLoadError((prev) => prev || 'Unable to load distributions from OMS APIs.');
    }

    setIsLoading(false);
  }, []);

  const loadCommitmentWorkspace = useCallback(async (commitmentId?: number) => {
    if (!commitmentId) {
      setCommitmentWorkspace(null);
      return;
    }
    setWorkspaceLoading(true);
    try {
      const response = await axios.get(`/api/alternatives/commitments/${commitmentId}/workspace`);
      setCommitmentWorkspace(response.data);
    } catch (error) {
      console.error('Failed to fetch commitment workspace:', error);
      setCommitmentWorkspace(null);
    } finally {
      setWorkspaceLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const commitmentId =
      selectedCall?.commitmentId ||
      selectedDistribution?.commitmentId ||
      selectedCommitment?.id;

    if (commitmentId) {
      loadCommitmentWorkspace(commitmentId);
    } else {
      setCommitmentWorkspace(null);
    }
  }, [selectedCommitment, selectedCall, selectedDistribution, loadCommitmentWorkspace]);

  // Capital call actions
  const handleApproveCall = async (callId: number) => {
    if (!user?.id) {
      setLoadError('User identity unavailable for approval.');
      return;
    }
    try {
      const commitmentId = capitalCalls.find(c => c.id === callId)?.commitmentId;
      await axios.post(`/api/alternatives/capital-calls/${callId}/approve?userId=${user.id}`);
      fetchData();
      loadCommitmentWorkspace(commitmentId);
    } catch (error) {
      console.error('Failed to approve call:', error);
    }
  };

  const handleInitiateFunding = async (callId: number) => {
    try {
      const commitmentId = capitalCalls.find(c => c.id === callId)?.commitmentId;
      await axios.post(`/api/alternatives/capital-calls/${callId}/initiate-funding?paymentReference=WIRE-${Date.now()}`);
      fetchData();
      loadCommitmentWorkspace(commitmentId);
    } catch (error) {
      setCapitalCalls(prev => prev.map(c => c.id === callId ? { ...c, status: 'FUNDING_INITIATED' as const } : c));
    }
  };

  // Confirm that wire has been executed by custodian
  const handleConfirmFunded = async (callId: number) => {
    try {
      const today = getTodayDate();
      const call = capitalCalls.find(c => c.id === callId);
      const amount = call?.callAmount || 0;
      await axios.post(`/api/alternatives/capital-calls/${callId}/confirm-funding?paymentDate=${today}&paymentAmount=${amount}`);
      fetchData();
      loadCommitmentWorkspace(call?.commitmentId);
    } catch (error) {
      console.error('Failed to confirm funding:', error);
      setCapitalCalls(prev => prev.map(c => c.id === callId ? { ...c, status: 'FUNDED' as const } : c));
    }
  };

  // Mark as reconciled after GP confirms receipt
  const handleReconcile = async (callId: number) => {
    try {
      const commitmentId = capitalCalls.find(c => c.id === callId)?.commitmentId;
      await axios.post(`/api/alternatives/capital-calls/${callId}/reconcile`);
      fetchData();
      loadCommitmentWorkspace(commitmentId);
    } catch (error) {
      console.error('Failed to reconcile:', error);
      setCapitalCalls(prev => prev.map(c => c.id === callId ? { ...c, status: 'RECONCILED' as const } : c));
    }
  };

  // Distribution actions
  const handleReviewDistribution = async (distId: number) => {
    if (!user?.id) {
      setLoadError('User identity unavailable for review.');
      return;
    }
    try {
      const commitmentId = distributions.find(d => d.id === distId)?.commitmentId;
      await axios.post(`/api/alternatives/distributions/${distId}/review?userId=${user.id}`);
      fetchData();
      loadCommitmentWorkspace(commitmentId);
    } catch (error) {
      console.error('Failed to review distribution:', error);
    }
  };

  const handleApproveDistribution = async (distId: number) => {
    if (!user?.id) {
      setLoadError('User identity unavailable for approval.');
      return;
    }
    try {
      const commitmentId = distributions.find(d => d.id === distId)?.commitmentId;
      await axios.post(`/api/alternatives/distributions/${distId}/approve?userId=${user.id}`);
      fetchData();
      loadCommitmentWorkspace(commitmentId);
    } catch (error) {
      console.error('Failed to approve distribution:', error);
    }
  };

  const handleConfirmReceipt = async (distId: number) => {
    try {
      const commitmentId = distributions.find(d => d.id === distId)?.commitmentId;
      await axios.post(`/api/alternatives/distributions/${distId}/confirm-receipt`);
      fetchData();
      loadCommitmentWorkspace(commitmentId);
    } catch (error) {
      console.error('Failed to confirm receipt:', error);
      setDistributions(prev => prev.map(d => d.id === distId ? { ...d, status: 'RECEIVED' as const } : d));
    }
  };

  const handleReconcileDistribution = async (distId: number) => {
    try {
      const commitmentId = distributions.find(d => d.id === distId)?.commitmentId;
      await axios.post(`/api/alternatives/distributions/${distId}/reconcile`);
      fetchData();
      loadCommitmentWorkspace(commitmentId);
    } catch (error) {
      console.error('Failed to reconcile distribution:', error);
      setDistributions(prev => prev.map(d => d.id === distId ? { ...d, status: 'RECONCILED' as const } : d));
    }
  };

  const toggleCommitmentExpand = (id: number) => {
    const newExpanded = new Set(expandedCommitments);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCommitments(newExpanded);
  };

  // Generate AI insights for selected item
  const getAIInsights = (item: CapitalCall | Distribution | null, type: 'call' | 'distribution') => {
    if (!item) return null;

    if (type === 'call') {
      const call = item as CapitalCall;
      const isOverdue = new Date(call.dueDate) < fromIso(getTenantToday()) && !['FUNDED', 'RECONCILED'].includes(call.status);
      return {
        riskLevel: isOverdue ? 'HIGH' : call.callAmount > 100000 ? 'MEDIUM' : 'LOW',
        cashImpact: `-${formatCurrency(call.callAmount)}`,
        recommendations: [
          isOverdue ? '⚠️ URGENT: This capital call is overdue. Process immediately to avoid penalties.' : '✓ Capital call is within due date.',
          call.callAmount > 100000 ? 'Consider reviewing cash reserves before funding.' : 'Amount is within normal range.',
          `Verify wire instructions with ${call.fundName} before initiating transfer.`,
          'Document approval in compliance records.'
        ],
        complianceChecks: [
          { check: 'Within commitment limit', status: 'pass' },
          { check: 'Sufficient cash available', status: call.callAmount > 500000 ? 'warning' : 'pass' },
          { check: 'Wire instructions verified', status: call.status === 'APPROVED' ? 'pass' : 'pending' },
          { check: 'Dual approval required', status: call.callAmount > 250000 ? 'required' : 'not_required' }
        ]
      };
    } else {
      const dist = item as Distribution;
      const totalAmount = dist.totalAmount || dist.grossDistribution || 0;
      return {
        riskLevel: 'LOW',
        cashImpact: `+${formatCurrency(totalAmount)}`,
        recommendations: [
          '✓ Verify distribution amount matches GP statement.',
          `Tax impact: ~${formatCurrency(totalAmount * 0.25)} estimated (consult tax advisor).`,
          dist.returnOfCapital ? `Return of capital: ${formatCurrency(dist.returnOfCapital)} reduces cost basis.` : 'Review waterfall allocation details.',
          'Update portfolio NAV after reconciliation.'
        ],
        complianceChecks: [
          { check: 'Amount matches notice', status: 'pass' },
          { check: 'Waterfall allocation verified', status: dist.capitalGains ? 'pass' : 'pending' },
          { check: 'Tax characterization complete', status: dist.status === 'REVIEWED' ? 'pass' : 'pending' },
          { check: 'Cash receipt confirmed', status: dist.status === 'RECEIVED' ? 'pass' : 'pending' }
        ]
      };
    }
  };

  return (
    <Container>
      <div className="flex gap-6">
        {/* Main Content */}
        <div className={cn("flex-1 transition-all duration-300", (selectedCall || selectedDistribution || selectedCommitment) && "max-w-[calc(100%-400px)]")}>
      {loadError && (
        <div className="mb-4 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm text-rose-300">
          {loadError}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Alternative Assets</h1>
            <p className="text-slate-400 text-sm">PE, VC, Real Estate, and Hedge Fund investments</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-lg text-sm font-medium text-slate-300 hover:text-white transition-all flex items-center gap-2"
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
            Refresh
          </button>
          {(activeTab === 'overview' || activeTab === 'commitments') && (
            /* D2 (p1-consolidation-decisions.md): commitment ACQUISITION is a
               governed ticket, not a back-office form — this ops surface keeps
               the approval/funding/reconcile queues only. */
            <button
              onClick={() => navigate('/trade-entry/private-equity')}
              className="px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-sm font-bold text-white transition-all flex items-center gap-2 shadow-lg shadow-violet-600/20"
              title="Commitments are created through the governed PE ticket"
            >
              <Plus className="w-4 h-4" />
              New Commitment (Ticket) →
            </button>
          )}
          {(activeTab === 'calls' || activeTab === 'distributions') && (
            <button
              onClick={() => {
                if (onNavigateToDocumentInbox) {
                  onNavigateToDocumentInbox();
                } else {
                  navigate('/middle-office?tab=document-inbox');
                }
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-bold text-white transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20"
            >
              <FileText className="w-4 h-4" />
              Import from Documents
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-4 mb-6">
        <MetricCard>
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <Target className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Total Commitment</span>
          </div>
          <div className="text-2xl font-bold text-white">{formatCurrency(totalCommitment)}</div>
          <div className="text-xs text-slate-500 mt-1">{commitments.length} active funds</div>
        </MetricCard>

        <MetricCard>
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <Banknote className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Called Capital</span>
          </div>
          <div className="text-2xl font-bold text-blue-400">{formatCurrency(totalCalled)}</div>
          <div className="text-xs text-slate-500 mt-1">{formatPercent(totalCalled / totalCommitment)} of commitment</div>
        </MetricCard>

        <MetricCard>
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <Wallet className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Unfunded</span>
          </div>
          <div className="text-2xl font-bold text-amber-400">{formatCurrency(unfundedCommitment)}</div>
          <div className="text-xs text-slate-500 mt-1">Remaining commitment</div>
        </MetricCard>

        <MetricCard>
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <ArrowDownRight className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Distributions</span>
          </div>
          <div className="text-2xl font-bold text-emerald-400">{formatCurrency(totalDistributed)}</div>
          <div className="text-xs text-slate-500 mt-1">Total received</div>
        </MetricCard>

        <MetricCard>
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <BarChart3 className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Current NAV</span>
          </div>
          <div className="text-2xl font-bold text-violet-400">{formatCurrency(totalNAV)}</div>
          <div className="text-xs text-slate-500 mt-1">Mark-to-market value</div>
        </MetricCard>

        <MetricCard>
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Pending Calls</span>
          </div>
          <div className="text-2xl font-bold text-rose-400">{pendingCalls.length}</div>
          <div className="text-xs text-slate-500 mt-1">
            {overdueCalls.length > 0 && <span className="text-rose-400">{overdueCalls.length} overdue</span>}
            {overdueCalls.length === 0 && 'None overdue'}
          </div>
        </MetricCard>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-slate-900/50 rounded-lg w-fit mb-6">
        {[
          { id: 'overview', label: 'Overview', icon: PieChart },
          { id: 'commitments', label: 'Commitments', icon: Target },
          { id: 'calls', label: 'Capital Calls', icon: ArrowUpRight },
          { id: 'distributions', label: 'Distributions', icon: ArrowDownRight }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2",
              activeTab === tab.id
                ? "bg-violet-600 text-white shadow-lg"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-2 gap-6"
          >
            {/* Commitments by Type */}
            <GlassCard>
              <div className="p-4 border-b border-white/5">
                <h3 className="text-sm font-bold text-white">Commitments by Asset Class</h3>
              </div>
              <div className="p-4 space-y-3">
                {['PRIVATE_EQUITY', 'VENTURE_CAPITAL', 'REAL_ESTATE', 'PRIVATE_CREDIT', 'INFRASTRUCTURE'].map(type => {
                  const typeCommitments = commitments.filter(c => c.fundType === type);
                  const typeTotal = typeCommitments.reduce((sum, c) => sum + c.commitmentAmount, 0);
                  const percentage = totalCommitment > 0 ? (typeTotal / totalCommitment) * 100 : 0;

                  if (typeTotal === 0) return null;

                  return (
                    <div key={type} className="flex items-center gap-4">
                      <div className={cn("px-2 py-1 rounded text-xs font-medium", getFundTypeColor(type))}>
                        {type.replace(/_/g, ' ')}
                      </div>
                      <div className="flex-1">
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-sm font-medium text-white w-24 text-right">{formatCurrency(typeTotal)}</div>
                      <div className="text-xs text-slate-500 w-12 text-right">{percentage.toFixed(0)}%</div>
                    </div>
                  );
                })}
              </div>
            </GlassCard>

            {/* Upcoming Capital Calls */}
            <GlassCard>
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white">Upcoming Capital Calls</h3>
                <span className="text-xs text-slate-500">Next 30 days</span>
              </div>
              <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto">
                {capitalCalls
                  .filter(c => ['PENDING', 'NOTICE_RECEIVED', 'REVIEWED', 'APPROVED', 'FUNDING_INITIATED'].includes(c.status))
                  .slice(0, 5)
                  .map(call => (
                    <div key={call.id} className="p-4 hover:bg-white/5 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">{call.fundName}</span>
                        {getCallStatusBadge(call.status)}
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">Due: {formatDate(call.dueDate)}</span>
                        <span className="text-lg font-bold text-rose-400">{formatCurrency(call.callAmount)}</span>
                      </div>
                    </div>
                  ))}
                {capitalCalls.filter(c => ['PENDING', 'NOTICE_RECEIVED', 'REVIEWED', 'APPROVED'].includes(c.status)).length === 0 && (
                  <div className="p-8 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500/50 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No pending capital calls</p>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* Performance Summary */}
            <GlassCard className="col-span-2">
              <div className="p-4 border-b border-white/5">
                <h3 className="text-sm font-bold text-white">Portfolio Performance</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">IRR</div>
                    <div className="text-3xl font-bold text-emerald-400">18.4%</div>
                    <div className="text-xs text-slate-500 mt-1">Since inception</div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">TVPI</div>
                    <div className="text-3xl font-bold text-violet-400">1.42x</div>
                    <div className="text-xs text-slate-500 mt-1">Total value / paid-in</div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">DPI</div>
                    <div className="text-3xl font-bold text-blue-400">0.65x</div>
                    <div className="text-xs text-slate-500 mt-1">Distributions / paid-in</div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">MOIC</div>
                    <div className="text-3xl font-bold text-amber-400">1.38x</div>
                    <div className="text-xs text-slate-500 mt-1">Multiple on invested</div>
                  </div>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        )}

        {activeTab === 'commitments' && (
          <motion.div
            key="commitments"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <GlassCard>
              <div className="divide-y divide-white/5">
                {commitments.map(commitment => (
                  <div key={commitment.id}>
                    <div
                      className="p-4 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedCommitment(commitment);
                        setSelectedCall(null);
                        setSelectedDistribution(null);
                        toggleCommitmentExpand(commitment.id);
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-violet-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-white">{commitment.fundName}</span>
                            <span className={cn("px-2 py-0.5 rounded text-[10px] font-medium", getFundTypeColor(commitment.fundType))}>
                              {commitment.fundType.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            {commitment.portfolioName} • Vintage {commitment.vintage}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-white">{formatCurrency(commitment.commitmentAmount)}</div>
                          <div className="text-xs text-slate-500">
                            {formatPercent(commitment.calledAmount / commitment.commitmentAmount)} called
                          </div>
                        </div>
                        <div className="w-32">
                          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full"
                              style={{ width: `${(commitment.calledAmount / commitment.commitmentAmount) * 100}%` }}
                            />
                          </div>
                        </div>
                        {expandedCommitments.has(commitment.id) ? (
                          <ChevronDown className="w-5 h-5 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-500" />
                        )}
                      </div>
                    </div>

                    {expandedCommitments.has(commitment.id) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="px-4 pb-4 bg-slate-900/30"
                      >
                        <div className="grid grid-cols-5 gap-4 p-4 bg-slate-900/50 rounded-lg">
                          <div>
                            <div className="text-xs text-slate-500 mb-1">Called</div>
                            <div className="text-lg font-bold text-blue-400">{formatCurrency(commitment.calledAmount)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500 mb-1">Remaining</div>
                            <div className="text-lg font-bold text-amber-400">{formatCurrency(commitment.remainingCommitment)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500 mb-1">Distributed</div>
                            <div className="text-lg font-bold text-emerald-400">{formatCurrency(commitment.distributedAmount)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500 mb-1">Current NAV</div>
                            <div className="text-lg font-bold text-violet-400">{formatCurrency(commitment.currentNAV)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500 mb-1">Status</div>
                            <span className={cn(
                              "inline-block px-2 py-1 rounded text-xs font-medium",
                              commitment.status === 'ACTIVE' && "bg-emerald-500/20 text-emerald-400",
                              commitment.status === 'FULLY_CALLED' && "bg-blue-500/20 text-blue-400",
                              commitment.status === 'CLOSED' && "bg-slate-500/20 text-slate-400"
                            )}>
                              {commitment.status.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}

        {activeTab === 'calls' && (
          <motion.div
            key="calls"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <GlassCard>
              <div className="divide-y divide-white/5">
                {capitalCalls.map(call => (
                  <div
                    key={call.id}
                    onClick={() => {
                      setSelectedCall(call);
                      setSelectedDistribution(null);
                      setSelectedCommitment(null);
                    }}
                    className={cn(
                      "p-4 hover:bg-white/5 transition-colors cursor-pointer",
                      selectedCall?.id === call.id && "bg-rose-500/10 border-l-2 border-rose-500"
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
                        <ArrowUpRight className="w-5 h-5 text-rose-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-white">{call.fundName}</span>
                          <span className="text-xs text-slate-500">Call #{call.callNumber}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {call.portfolioName} • Due {formatDate(call.dueDate)}
                        </div>
                        {call.purpose && (
                          <div className="text-xs text-slate-400 mt-1">{call.purpose}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-rose-400">{formatCurrency(call.callAmount)}</div>
                      </div>
                      {getCallStatusBadge(call.status)}
                      <div className="flex items-center gap-2">
                        {['PENDING', 'NOTICE_RECEIVED', 'REVIEWED'].includes(call.status) && (
                          <button
                            onClick={() => handleApproveCall(call.id)}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs font-bold text-white transition-colors"
                          >
                            Approve
                          </button>
                        )}
                        {call.status === 'APPROVED' && (
                          <button
                            onClick={() => handleInitiateFunding(call.id)}
                            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-xs font-bold text-white transition-colors"
                          >
                            Send Wire
                          </button>
                        )}
                        {call.status === 'FUNDING_INITIATED' && (
                          <button
                            onClick={() => handleConfirmFunded(call.id)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-bold text-white transition-colors"
                          >
                            Confirm Funded
                          </button>
                        )}
                        {call.status === 'FUNDED' && (
                          <button
                            onClick={() => handleReconcile(call.id)}
                            className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 rounded-lg text-xs font-bold text-white transition-colors"
                          >
                            Mark Reconciled
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}

        {activeTab === 'distributions' && (
          <motion.div
            key="distributions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <GlassCard>
              {distributions.length === 0 ? (
                <div className="p-12 text-center">
                  <ArrowDownRight className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-300 mb-2">No Distributions</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    No distribution notices have been imported yet.
                  </p>
                  <button
                    onClick={() => {
                      if (onNavigateToDocumentInbox) {
                        onNavigateToDocumentInbox();
                      } else {
                        navigate('/middle-office?tab=document-inbox');
                      }
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold text-white transition-colors inline-flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Import Distribution Notice
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {distributions.map(dist => (
                    <div
                      key={dist.id}
                      onClick={() => {
                        setSelectedDistribution(dist);
                        setSelectedCall(null);
                        setSelectedCommitment(null);
                      }}
                      className={cn(
                        "p-4 hover:bg-white/5 transition-colors cursor-pointer",
                        selectedDistribution?.id === dist.id && "bg-emerald-500/10 border-l-2 border-emerald-500"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <ArrowDownRight className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-white">{dist.fundName || 'Distribution'}</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            {dist.portfolioName || 'Portfolio'} • {formatDate(dist.distributionDate)}
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-center">
                          <div>
                            <div className="text-[10px] text-slate-500 uppercase">ROC</div>
                            <div className="text-sm font-medium text-slate-300">{formatCurrency(dist.returnOfCapital || 0)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-slate-500 uppercase">Gains</div>
                            <div className="text-sm font-medium text-emerald-400">{formatCurrency(dist.capitalGains || 0)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-slate-500 uppercase">Dividends</div>
                            <div className="text-sm font-medium text-blue-400">{formatCurrency(dist.dividends || (dist.qualifiedDividends || 0) + (dist.unqualifiedDividends || 0))}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-slate-500 uppercase">Total</div>
                            <div className="text-lg font-bold text-emerald-400">{formatCurrency(dist.totalAmount || dist.grossDistribution || 0)}</div>
                          </div>
                        </div>
                        {getCallStatusBadge(dist.status)}
                        <div className="flex items-center gap-2">
                          {['NOTICE_RECEIVED'].includes(dist.status) && (
                            <button
                              onClick={() => handleReviewDistribution(dist.id)}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-bold text-white transition-colors"
                            >
                              Review
                            </button>
                          )}
                          {dist.status === 'REVIEWED' && (
                            <button
                              onClick={() => handleApproveDistribution(dist.id)}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs font-bold text-white transition-colors"
                            >
                              Approve
                            </button>
                          )}
                          {dist.status === 'APPROVED' && (
                            <button
                              onClick={() => handleConfirmReceipt(dist.id)}
                              className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-xs font-bold text-white transition-colors"
                            >
                              Confirm Receipt
                            </button>
                          )}
                          {dist.status === 'RECEIVED' && (
                            <button
                              onClick={() => handleReconcileDistribution(dist.id)}
                              className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 rounded-lg text-xs font-bold text-white transition-colors"
                            >
                              Reconcile
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>
        </div>

        {/* AI Review Panel */}
        <AnimatePresence>
          {(selectedCall || selectedDistribution || selectedCommitment) && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="w-[380px] flex-shrink-0"
            >
              <GlassCard className="sticky top-6">
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white">Ops Workspace</h3>
                      <p className="text-[10px] text-slate-500">Commitment context and review</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedCall(null);
                      setSelectedDistribution(null);
                      setSelectedCommitment(null);
                    }}
                    className="p-1.5 hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>

                {(() => {
                  const insights = selectedCall
                    ? getAIInsights(selectedCall, 'call')
                    : selectedDistribution
                      ? getAIInsights(selectedDistribution, 'distribution')
                      : null;

                  return (
                    <div className="p-4 space-y-4">
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Commitment Workspace</div>
                        {workspaceLoading ? (
                          <div className="text-sm text-slate-400">Loading commitment context...</div>
                        ) : commitmentWorkspace ? (
                          <>
                            <div className="text-sm font-bold text-white">
                              {commitmentWorkspace.fundName || selectedCommitment?.fundName || 'Selected Fund'}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {commitmentWorkspace.portfolioCode || commitmentWorkspace.portfolioName || selectedCommitment?.portfolioName || 'Portfolio'}
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                              <div>
                                <div className="text-slate-500">Called</div>
                                <div className="text-blue-400 font-bold">{formatCurrency(commitmentWorkspace.calledAmount)}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Unfunded</div>
                                <div className="text-amber-400 font-bold">{formatCurrency(commitmentWorkspace.unfundedCommitment)}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Distributed</div>
                                <div className="text-emerald-400 font-bold">{formatCurrency(commitmentWorkspace.distributedAmount)}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Status</div>
                                <div className="text-white font-bold">{commitmentWorkspace.status.replace(/_/g, ' ')}</div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-slate-500">No commitment workspace available.</div>
                        )}
                      </div>

                      {commitmentWorkspace && commitmentWorkspace.recentEvents.length > 0 && (
                        <div>
                          <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Recent Event History</div>
                          <div className="space-y-2">
                            {commitmentWorkspace.recentEvents.slice(0, 5).map((event) => (
                              <div key={event.eventId} className="p-2 bg-slate-900/40 rounded-lg">
                                <div className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-white font-medium">{event.eventType.replace(/_/g, ' ')}</span>
                                  <span className="text-slate-500">{event.eventDate || '-'}</span>
                                </div>
                                <div className="flex items-center justify-between gap-2 text-xs mt-1">
                                  <span className="text-slate-400">{event.status}</span>
                                  <span className="text-slate-300">{event.eventAmount ? formatCurrency(event.eventAmount) : '-'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {insights && (
                        <>
                      {/* Item Summary */}
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                          {selectedCall ? 'Capital Call' : 'Distribution'}
                        </div>
                        <div className="text-sm font-bold text-white">
                          {selectedCall?.fundName || selectedDistribution?.fundName || 'Unknown Fund'}
                        </div>
                        <div className="text-lg font-bold mt-1" style={{ color: selectedCall ? '#f87171' : '#34d399' }}>
                          {insights.cashImpact}
                        </div>
                      </div>

                      {/* Risk Assessment */}
                      <div>
                        <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Risk Assessment</div>
                        <div className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold",
                          insights.riskLevel === 'HIGH' && "bg-red-500/20 text-red-400",
                          insights.riskLevel === 'MEDIUM' && "bg-amber-500/20 text-amber-400",
                          insights.riskLevel === 'LOW' && "bg-emerald-500/20 text-emerald-400"
                        )}>
                          {insights.riskLevel === 'HIGH' && <AlertTriangle className="w-3 h-3" />}
                          {insights.riskLevel === 'MEDIUM' && <Clock className="w-3 h-3" />}
                          {insights.riskLevel === 'LOW' && <CheckCircle className="w-3 h-3" />}
                          {insights.riskLevel} RISK
                        </div>
                      </div>

                      {/* Compliance Checks */}
                      <div>
                        <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Compliance Checks</div>
                        <div className="space-y-2">
                          {insights.complianceChecks.map((check, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs">
                              <span className="text-slate-300">{check.check}</span>
                              <span className={cn(
                                "px-1.5 py-0.5 rounded text-[10px] font-medium",
                                check.status === 'pass' && "bg-emerald-500/20 text-emerald-400",
                                check.status === 'warning' && "bg-amber-500/20 text-amber-400",
                                check.status === 'pending' && "bg-slate-500/20 text-slate-400",
                                check.status === 'required' && "bg-blue-500/20 text-blue-400",
                                check.status === 'not_required' && "bg-slate-500/20 text-slate-500"
                              )}>
                                {check.status === 'pass' ? '✓ Pass' :
                                 check.status === 'warning' ? '⚠ Review' :
                                 check.status === 'pending' ? '○ Pending' :
                                 check.status === 'required' ? '● Required' : '○ N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                        </>
                      )}

                      {insights && (
                        <div>
                          <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">AI Recommendations</div>
                          <div className="space-y-2">
                            {insights.recommendations.map((rec, idx) => (
                              <div key={idx} className="text-xs text-slate-300 leading-relaxed p-2 bg-slate-900/30 rounded">
                                {rec}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="pt-2 border-t border-white/5">
                        {selectedCall && ['PENDING', 'NOTICE_RECEIVED', 'REVIEWED'].includes(selectedCall.status) && (
                          <button
                            onClick={() => {
                              handleApproveCall(selectedCall.id);
                              setSelectedCall(null);
                            }}
                            className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold text-white transition-colors"
                          >
                            Approve Capital Call
                          </button>
                        )}
                        {selectedDistribution && selectedDistribution.status === 'NOTICE_RECEIVED' && (
                          <button
                            onClick={() => {
                              handleReviewDistribution(selectedDistribution.id);
                              setSelectedDistribution(null);
                            }}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-bold text-white transition-colors"
                          >
                            Mark as Reviewed
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </Container>
  );
};

export default AlternativeAssetsScreen;
